import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type ConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  requireTyping?: boolean;
  loading?: boolean;
  variant?: "destructive" | "warning";
};

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  requireTyping = false,
  loading = false,
  variant = "warning",
}: ConfirmModalProps) {
  const [typedValue, setTypedValue] = useState("");

  // Reset typed value whenever the modal is opened
  useEffect(() => {
    if (open) setTypedValue("");
  }, [open]);

  const canConfirm = !requireTyping || typedValue === "CONFIRM";

  function handleConfirm() {
    if (!canConfirm || loading) return;
    onConfirm();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        {variant === "destructive" && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-4 py-3 -mt-1 mb-1">
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-sm font-medium text-red-700">
              This action cannot be undone.
            </p>
          </div>
        )}
        {variant === "warning" && (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 -mt-1 mb-1">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <p className="text-sm font-medium text-amber-700">
              Please review before continuing.
            </p>
          </div>
        )}

        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {requireTyping && (
          <div className="space-y-2">
            <Label htmlFor="confirm-input" className="text-sm text-gray-700">
              Type <span className="font-mono font-semibold">CONFIRM</span> to proceed
            </Label>
            <Input
              id="confirm-input"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder="CONFIRM"
              className={cn(
                typedValue.length > 0 && typedValue !== "CONFIRM"
                  ? "border-red-300 focus-visible:ring-red-300"
                  : "",
              )}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
          >
            {loading ? "Processing…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
